import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const GammaRayBurst = ({ position, scale = 1 }) => {
    const groupRef = useRef();
    const coreRef = useRef();
    const jetsRef = useRef();
    const shockwaveRef = useRef();

    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        
        // 1. Violent Core Instability
        if (coreRef.current) {
            // Rapid, erratic pulsing
            const scalePulse = 1 + Math.sin(t * 30) * 0.05 + Math.cos(t * 45) * 0.05;
            coreRef.current.scale.setScalar(scalePulse);
        }

        // 2. Jet Flow Animation
        // (Simulated by scaling/intensity flicker if we had a shader, 
        // here we just wobble the beam slightly to show power)
        if (jetsRef.current) {
            // The jets are stable in direction but might fluctuate in width
            const widthPulse = 1 + Math.sin(t * 50) * 0.02;
            jetsRef.current.scale.set(widthPulse, 1, widthPulse);
        }

        // 3. Expanding Shockwaves
        if (shockwaveRef.current) {
            shockwaveRef.current.rotation.z -= 0.02;
            // Pulse the rings opacity/size slightly
            const ringPulse = 1 + Math.sin(t * 10) * 0.05;
            shockwaveRef.current.scale.setScalar(ringPulse);
        }
    });

    return (
        <group ref={groupRef} position={position} scale={scale}>
            {/* 1. The Collapsing Star Core (Blinding White) */}
            <mesh ref={coreRef}>
                <sphereGeometry args={[0.45, 32, 32]} />
                <meshPhysicalMaterial 
                    color="#ffffff"
                    emissive="#ffffff"
                    emissiveIntensity={20} // Blinding
                    toneMapped={false}
                />
                <pointLight distance={10} intensity={8} color="#a855f7" decay={1} />
            </mesh>

            {/* 2. The GRB Beams (Infinite Range) */}
            <group ref={jetsRef}>
                {/* Top Beam - Short and Wider */}
                <mesh position={[0, 0.75, 0]}> {/* Adjusted position to be shorter */}
                    <cylinderGeometry args={[0.08, 0.2, 1.5, 16, 1, true]} /> 
                    <meshBasicMaterial 
                        color="#d8b4fe" 
                        transparent 
                        opacity={0.8} 
                        blending={THREE.AdditiveBlending} 
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
                {/* Inner Core of Beam (Pure White Energy) */}
                <mesh position={[0, 0.75, 0]}> {/* Adjusted position to be shorter */}
                    <cylinderGeometry args={[0.02, 0.05, 1.5, 16, 1, true]} /> 
                    <meshBasicMaterial 
                        color="#ffffff" 
                        transparent 
                        opacity={1.0} 
                        blending={THREE.AdditiveBlending} 
                        depthWrite={false}
                    />
                </mesh>

                {/* Bottom Beam */}
                <mesh position={[0, -0.75, 0]} rotation={[Math.PI, 0, 0]}> {/* Adjusted position to be shorter */}
                    <cylinderGeometry args={[0.08, 0.2, 1.5, 16, 1, true]} /> 
                    <meshBasicMaterial 
                        color="#d8b4fe" 
                        transparent 
                        opacity={0.8} 
                        blending={THREE.AdditiveBlending} 
                        side={THREE.DoubleSide}
                        depthWrite={false}
                    />
                </mesh>
                {/* Inner Core Bottom */}
                <mesh position={[0, -0.75, 0]} rotation={[Math.PI, 0, 0]}> {/* Adjusted position to be shorter */}
                    <cylinderGeometry args={[0.02, 0.05, 1.5, 16, 1, true]} /> 
                    <meshBasicMaterial 
                        color="#ffffff" 
                        transparent 
                        opacity={1.0} 
                        blending={THREE.AdditiveBlending} 
                        depthWrite={false}
                    />
                </mesh>
            </group>
            
            {/* 3. Equatorial Shockwaves */}
            <group ref={shockwaveRef} rotation={[Math.PI / 2, 0, 0]}>
                {/* Inner fast ring */}
                <mesh>
                    <ringGeometry args={[0.6, 0.8, 32]} />
                    <meshBasicMaterial color="#c084fc" transparent opacity={0.6} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} />
                </mesh>
                {/* Outer expanding ring */}
                <mesh>
                    <ringGeometry args={[1.2, 1.8, 32]} />
                    <meshBasicMaterial color="#a855f7" transparent opacity={0.3} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} />
                </mesh>
                {/* Debris field / Sparkles */}
                <points>
                    <sphereGeometry args={[2.5, 32, 32]} />
                    <pointsMaterial size={0.05} color="#e9d5ff" transparent opacity={0.4} blending={THREE.AdditiveBlending} />
                </points>
            </group>

        </group>
    );
};

export default GammaRayBurst;